import axios from 'axios'

const MAX_IMAGE_PIXELS = 30_000_000
const MAX_IMAGE_BYTES = 9 * 1024 * 1024
const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif'])
const SUPPORTED_VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime'])

function getFileExtension(name = '') {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index + 1).toLowerCase() : ''
}

function isSupportedImage(file) {
  return SUPPORTED_IMAGE_TYPES.has(file.type) || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(getFileExtension(file.name))
}

function isSupportedVideo(file) {
  return SUPPORTED_VIDEO_TYPES.has(file.type) || ['mp4', 'webm', 'mov'].includes(getFileExtension(file.name))
}

function resolveContentType(file, isVideo) {
  const extension = getFileExtension(file.name)
  const imageTypes = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' }
  const videoTypes = { mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' }
  const expectedTypes = isVideo ? SUPPORTED_VIDEO_TYPES : SUPPORTED_IMAGE_TYPES
  if (expectedTypes.has(file.type)) return file.type
  return (isVideo ? videoTypes : imageTypes)[extension] || 'application/octet-stream'
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法读取图片，请选择有效的图片文件'))
    }
    image.src = url
  })
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('图片压缩失败'))), 'image/jpeg', quality)
  })
}

/** 与旧后端策略一致：超过 3000 万像素或 9MB 时再缩放和 JPEG 压缩。 */
async function optimizeImage(file) {
  if (!isSupportedImage(file)) throw new Error('仅支持 JPG、PNG、WebP、GIF 图片格式')
  // GIF 保留动画；不将其重编码为静态 JPEG。
  if (file.type === 'image/gif' || getFileExtension(file.name) === 'gif') return file

  const image = await loadImage(file)
  const pixels = image.naturalWidth * image.naturalHeight
  if (pixels <= MAX_IMAGE_PIXELS && file.size <= MAX_IMAGE_BYTES) return file

  const ratio = pixels > MAX_IMAGE_PIXELS ? Math.sqrt(MAX_IMAGE_PIXELS / pixels) : 1
  const width = Math.max(1, Math.round(image.naturalWidth * ratio))
  const height = Math.max(1, Math.round(image.naturalHeight * ratio))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  // JPEG 不支持透明通道，白色底图避免 PNG/WebP 的透明区域变黑。
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
  context.drawImage(image, 0, 0, width, height)

  let quality = 0.9
  let blob = await canvasToBlob(canvas, quality)
  while (blob.size > MAX_IMAGE_BYTES && quality > 0.45) {
    quality -= 0.1
    blob = await canvasToBlob(canvas, quality)
  }
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
}

export async function uploadFileToOss(http, file, options = {}) {
  const isVideo = Boolean(options.isVideo)
  if (!(file instanceof File)) throw new Error('未找到待上传文件')
  if (isVideo && !isSupportedVideo(file)) throw new Error('仅支持 MP4、WebM、MOV 视频格式')
  const preparedFile = isVideo ? file : await optimizeImage(file)
  const contentType = resolveContentType(preparedFile, isVideo)
  const token = localStorage.getItem('token')
  const authHeaders = token ? { authorization: `Bearer ${token}` } : {}

  const signatureResponse = await http.post('/ai/upload/presign', {
    fileName: preparedFile.name,
    contentType,
    fileSize: preparedFile.size,
    video: isVideo
  }, { headers: authHeaders, skipDirectOssUpload: true })
  const signature = signatureResponse.data && signatureResponse.data.data
  if (!signature || !signature.uploadUrl || !signature.objectKey) {
    throw new Error(signatureResponse.data?.msg || '获取 OSS 上传凭证失败')
  }

  try {
    await axios.put(signature.uploadUrl, preparedFile, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: options.onUploadProgress
    })
  } catch (error) {
    const status = error.response?.status
    const uploadError = new Error(status === 403
      ? 'OSS 上传签名校验失败，请重启后端后重试'
      : '文件直传 OSS 失败，请检查网络后重试')
    uploadError.isDirectOssUploadError = true
    uploadError.cause = error
    throw uploadError
  }

  const recordResponse = await http.post('/ai/upload/record', {
    objectKey: signature.objectKey,
    fileUrl: signature.fileUrl,
    fileName: preparedFile.name,
    fileSize: preparedFile.size,
    video: isVideo
  }, { headers: authHeaders, skipDirectOssUpload: true })
  return recordResponse.data
}
